import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  output,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { deepEqual } from 'fast-equals';
import { BehaviorSubject, startWith, Subject, switchMap, takeUntil } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { DateRangeValueType } from '@core/date-range-types';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  AlwaysOpenStrategyDirective,
  DropdownWidth,
  OverlayOriginDirective,
  OverlayPosition,
  SearchFeatureDirective,
  Select2Component,
  Select2TriggerComponent,
  Select2TriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { InputOptionsState, OptionsQueryParams } from '@core/views-data';

import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import type { DatePickerRelativeCustom, DatePickerValue } from './date-range-picker/types';
import { RelativeUnit } from './date-range-picker/types';
import type { InputSelectFormValue, InputSelectOptions, InputSelectSavedValue } from './types';
import type { InputTypeComponent } from '../../advanced-filters/filter/types';

export type SelectComponentState = Select2Item<InputSelectFormValue>;

type InputSelect = InputTypeComponent<
  FormControl<InputSelectFormValue | null>,
  SelectComponentState,
  InputSelectOptions
>;

export const optionToSelectItem = (option: InputSelectSavedValue) => ({
  label: option.label.text,
  value: <InputSelectFormValue>{ ...option.value, type: option.type },
});

@Component({
  selector: 'rt-views-input-select',
  imports: [
    ReactiveFormsModule,
    Select2Component,
    SearchFeatureDirective,
    Select2TriggerComponent,
    DateRangePickerComponent,
    OverlayOriginDirective,
    AlwaysOpenStrategyDirective,
    Select2TriggerDirective,
  ],
  templateUrl: './input-select.component.html',
  styleUrls: ['./input-select.component.less'],
})
export class InputSelectComponent implements InputSelect {
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly DropdownWidth = DropdownWidth;
  public readonly OverlayPosition = OverlayPosition;
  public readonly LOAD_MORE_WHEN_LEFT = 5;

  @Input() public inputControl: InputSelect['inputControl'];
  @Input() public optionsData: InputSelect['optionsData'];
  @Input() public mode: 'advanced' | 'quick' = 'advanced';
  @Input() public showBackButton$?: Subject<boolean>;
  @Input() public state: SelectComponentState;
  @Input() public placeholder: string;

  public search = output<string>();
  public loadMore = output<OptionsQueryParams>();
  @Output() public closed = new EventEmitter<void>();

  public state$ = new BehaviorSubject<SelectComponentState | null>(null);
  public optionOptions: Array<Select2Item<InputSelectFormValue>>;
  public searchControl = new FormControl('', { nonNullable: true });
  public hiddenItems: Array<Select2Item<InputSelectFormValue>>;
  public showDatePicker = false;
  public datePickerValue: DatePickerValue;
  public compareItems = this.getCompareItems();

  private inputControl$ = new BehaviorSubject<InputSelect['inputControl'] | null>(null);
  private untilDestroyed = injectUntilDestroyed();
  private showBackButtonChanged$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.optionsData) {
      this.optionOptions = this.optionsData ? this.getOptions(this.optionsData) : [];
    }

    if (changes.state) {
      this.hiddenItems = this.state ? [this.state] : [];
      this.state$.next(this.state);
    }

    if (changes.inputControl) {
      this.handleSelectedValue(this.inputControl.value);
      this.inputControl$.next(this.inputControl);
    }

    if (changes.showBackButton$) {
      this.showBackButtonChanged$.next();
      this.showBackButton$
        ?.pipe(takeUntil(this.showBackButtonChanged$), this.untilDestroyed)
        .subscribe(showBackButton => {
          this.showDatePicker = showBackButton;
          this.cdRef.detectChanges();
        });
    }
  }

  public ngOnInit() {
    this.searchControl.valueChanges.pipe(this.untilDestroyed).subscribe(query => {
      this.search.emit(query);
    });

    this.inputControl$
      .pipe(
        filterBoolean,
        switchMap(inputControl => inputControl.valueChanges.pipe(startWith(inputControl.value))),
        this.untilDestroyed,
      )
      .subscribe(value => {
        this.handleSelectedValue(value);
        this.cdRef.detectChanges();
        this.showBackButton$?.next(this.showDatePicker);
      });
  }

  public onScrollChange(event: IPageInfo) {
    if (!this.optionsData) return;

    const { page, limit } = this.optionsData.meta;
    const isCloseToScrollEnd =
      event.endIndexWithBuffer > this.optionOptions.length - this.LOAD_MORE_WHEN_LEFT;
    const canLoadMore = page * limit === this.optionOptions.length;
    if (isCloseToScrollEnd && canLoadMore) {
      this.loadMore.emit({
        page: page + 1,
        limit: limit,
        searchQuery: this.searchControl.value,
      });
    }
  }

  public onDateValueChange(event: DatePickerValue | null) {
    if (!event) {
      return this.inputControl.setValue(event);
    } else if (DateRangeValueType.Exact === event.type) {
      return this.inputControl.setValue({
        type: 'date',
        date: event.date,
      });
    } else if (DateRangeValueType.Range === event.type) {
      return this.inputControl.setValue({
        type: 'date_range',
        from: event.min,
        to: event.max,
      });
    } else if (
      DateRangeValueType.WithinLast === event.type ||
      DateRangeValueType.WithinNext === event.type
    ) {
      const data = <DatePickerRelativeCustom>event;
      return this.inputControl.setValue({
        type: 'date_within',
        modifier: data.type === DateRangeValueType.WithinLast ? 'prev' : 'next',
        amount: data.value,
        unit: data.unit,
      });
    }
  }

  private handleSelectedValue(value: InputSelectFormValue | null) {
    if (!value) return;

    if (value.type === 'date') {
      this.showDatePicker = true;
      this.datePickerValue = {
        type: DateRangeValueType.Exact,
        date: value.date,
      };
    } else if (value.type === 'date_range') {
      this.showDatePicker = true;
      this.datePickerValue = {
        type: DateRangeValueType.Range,
        min: value.from,
        max: value.to,
      };
    } else if (value.type === 'date_within') {
      this.showDatePicker = true;
      this.datePickerValue = {
        type:
          value.modifier === 'next' ? DateRangeValueType.WithinNext : DateRangeValueType.WithinLast,
        value: value.amount,
        unit: value.unit ?? RelativeUnit.Days,
      };
    } else {
      this.showDatePicker = false;
    }
  }

  private getOptions({ options }: InputOptionsState<InputSelectOptions>) {
    return options.map(option => optionToSelectItem(option));
  }

  private getCompareItems() {
    return <Item extends Select2Item<InputSelectFormValue>>(
      a: Item,
      b: Item | { value: Item['value'] | null },
    ) => {
      if (a.value?.type === 'date' && b.value?.type === 'date') return true;
      if (a.value?.type === 'date_range' && b.value?.type === 'date_range') return true;
      if (a.value?.type === 'date_within' && b.value?.type === 'date_within') {
        return a.value.modifier === b.value.modifier;
      }

      return deepEqual(a.value, b.value);
    };
  }
}
